import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Gesture } from 'react-native-gesture-handler';

import { updateReminder } from '../../api/client';
import { useBackHandler } from '../../navigation/useBackHandler';
import { useAuthStore } from '../../stores/authStore';
import { useCalendarStore } from '../../stores/calendarStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { useRemindersStore } from '../../stores/reminderStore';
import type { CalendarEvent, Reminder } from '../../types/api';
import { getLocale } from '../../utils/localization';
import DayNavigatorSheet from './DayNavigatorSheet';
import QuickAddFAB from './QuickAddFAB';

type Props = {
  visible: boolean;
  bottomSpace: number;
  onClose: () => void;
};

function reminderToCalendarEvent(reminder: Reminder): CalendarEvent | null {
  if (!reminder.run_at_utc) return null;
  return {
    type: 'reminder',
    seq: reminder.user_seq,
    body: reminder.body,
    runAtUtc: reminder.run_at_utc,
    isRecurring: !!reminder.is_recurring,
    isPaused: !!reminder.is_paused,
    sent: !!(reminder.is_done && !reminder.is_recurring),
    tags: reminder.tags,
    durationMinutes: reminder.duration_minutes ?? null,
    colPref: reminder.col_pref ?? null,
    recurJson: reminder.recur_json,
    untilUtc: reminder.until_utc,
    googleEventId: reminder.google_event_id ?? null,
    source: reminder.source ?? 'app',
  };
}

export default function GlobalDayNavigatorOverlay({ visible, bottomSpace, onClose }: Props) {
  const overlayId = useId();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const progress = useRef(new Animated.Value(0)).current;
  const [rendered, setRendered] = useState(visible);
  const viewMode = useNavigationStore((state) => state.globalDayNavigatorViewMode);
  const setViewMode = useNavigationStore((state) => state.setGlobalDayNavigatorViewMode);
  const [quickAddOpenSignal, setQuickAddOpenSignal] = useState(0);
  const [dragCreateTime, setDragCreateTime] = useState<{ startMinutes: number; durationMinutes: number } | null>(null);

  const reminders = useRemindersStore((state) => state.reminders);
  const remindersLoading = useRemindersStore((state) => state.isLoading);
  const syncFromServer = useRemindersStore((state) => state.syncFromServer);
  const googleEvents = useCalendarStore((state) => state.googleEvents);
  const calendarLoading = useCalendarStore((state) => state.isLoading);
  const loadGoogleEvents = useCalendarStore((state) => state.loadGoogleEvents);
  const selectedDate = useCalendarStore((state) => state.selectedDate);
  const setSelectedDate = useCalendarStore((state) => state.setSelectedDate);
  const tagColors = useAuthStore((state) => state.user?.tagColors ?? null);

  const allEvents = useMemo(() => {
    const reminderEvents = reminders
      .map(reminderToCalendarEvent)
      .filter((event): event is CalendarEvent => !!event);
    return [...reminderEvents, ...googleEvents]
      .filter((event) => !!event.runAtUtc)
      .sort((a, b) => a.runAtUtc.localeCompare(b.runAtUtc));
  }, [googleEvents, reminders]);

  const selectedDateTime = useMemo(() => {
    const parsed = DateTime.fromISO(selectedDate);
    return parsed.isValid ? parsed : DateTime.local().startOf('day');
  }, [selectedDate]);
  const selectedDateKey = selectedDateTime.toISODate() || DateTime.local().toISODate() || '';
  const dayEvents = useMemo(
    () => allEvents.filter((event) => DateTime.fromISO(event.runAtUtc).toLocal().toISODate() === selectedDateKey),
    [allEvents, selectedDateKey],
  );
  const selectedWeekdayLabel = selectedDateTime.setLocale(locale).toFormat('cccc');
  const selectedDateDisplay = selectedDateTime.setLocale(locale).toFormat('LLLL d');
  const selectedDateLabel = selectedDateTime.setLocale(locale).toFormat('cccc, LLLL d');
  const disabledGesture = useMemo(() => Gesture.Pan().enabled(false), []);

  useEffect(() => {
    let frame: number | undefined;
    if (visible) {
      setRendered(true);
      progress.stopAnimation();
      progress.setValue(0);
      frame = requestAnimationFrame(() => {
        Animated.timing(progress, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }).start();
      });
    } else if (rendered) {
      progress.stopAnimation();
      Animated.timing(progress, {
        toValue: 0,
        duration: 150,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) setRendered(false);
      });
    }
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress, rendered, visible]);

  useBackHandler({
    id: `global-day-navigator-${overlayId}`,
    label: 'Day navigator',
    priority: 850,
    enabled: visible,
    onBack: () => {
      onClose();
      return true;
    },
  });

  const selectDate = useCallback((dateKey: string) => {
    if (DateTime.fromISO(dateKey).isValid) setSelectedDate(dateKey);
  }, [setSelectedDate]);

  const navigateDate = useCallback((days: number) => {
    const current = DateTime.fromISO(useCalendarStore.getState().selectedDate);
    const base = current.isValid ? current : DateTime.local();
    const next = base.plus({ days }).toISODate();
    if (next) setSelectedDate(next);
  }, [setSelectedDate]);

  const refresh = useCallback(() => {
    syncFromServer();
    loadGoogleEvents(true);
  }, [loadGoogleEvents, syncFromServer]);

  const resizeEvent = useCallback(async (seq: number, durationMinutes: number) => {
    try {
      await updateReminder(seq, { durationMinutes });
      await syncFromServer();
    } catch (error) {
      console.warn('[GlobalDayNavigator] resize failed', error);
    }
  }, [syncFromServer]);

  const moveEvent = useCallback(async (seq: number, newRunAtUtc: string) => {
    try {
      await updateReminder(seq, { runAtUtc: newRunAtUtc });
      await syncFromServer();
    } catch (error) {
      console.warn('[GlobalDayNavigator] move failed', error);
    }
  }, [syncFromServer]);

  const changeColumn = useCallback(async (updates: Array<{ seq: number; colPref: number }>) => {
    try {
      await Promise.all(updates.map((update) => updateReminder(update.seq, { colPref: update.colPref })));
      await syncFromServer();
    } catch (error) {
      console.warn('[GlobalDayNavigator] change column failed', error);
    }
  }, [syncFromServer]);

  const openQuickAdd = useCallback(() => {
    setQuickAddOpenSignal((signal) => signal + 1);
  }, []);

  const openEvent = useCallback((event: CalendarEvent) => {
    if (!event.seq) return;
    router.push({ pathname: '/reminder/[seq]', params: { seq: String(event.seq), mode: 'edit' } });
  }, [router]);

  if (!rendered) return null;

  const animatedStyle = {
    opacity: progress,
    transform: [{
      translateY: progress.interpolate({
        inputRange: [0, 1],
        outputRange: [72, 0],
      }),
    }],
  };

  return (
    <>
      <Animated.View
        pointerEvents={visible ? 'auto' : 'none'}
        style={[
          styles.overlay,
          animatedStyle,
          { top: insets.top, bottom: bottomSpace },
        ]}
      >
        <DayNavigatorSheet
          dayEvents={dayEvents}
          allEvents={allEvents}
          selectedDate={selectedDateKey}
          selectedWeekdayLabel={selectedWeekdayLabel}
          selectedDateDisplay={selectedDateDisplay}
          selectedDateLabel={selectedDateLabel}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          onPressEvent={openEvent}
          onSelectDate={selectDate}
          onQuickAdd={openQuickAdd}
          onResizeEvent={resizeEvent}
          onMoveEvent={moveEvent}
          onChangeColumn={changeColumn}
          onDragCreate={(startMinutes, durationMinutes) => setDragCreateTime({ startMinutes, durationMinutes })}
          onNavigateDay={(direction) => navigateDate(direction)}
          onNavigateWeek={(direction) => navigateDate(direction * 7)}
          onToggleSheet={onClose}
          onRefresh={refresh}
          refreshing={remindersLoading || calendarLoading}
          tagColors={tagColors}
          bottomInset={0}
          headerGesture={disabledGesture}
          daySwipeGesture={disabledGesture}
          headerAnimatedStyle={{}}
          contentAnimatedStyle={{}}
          eventsSwipeAnimatedStyle={{}}
          dragToDismissEnabled
          isPresented={visible}
          fullScreen
        />
      </Animated.View>
      <QuickAddFAB
        selectedDate={selectedDateKey}
        bottomInset={insets.bottom}
        onCreated={refresh}
        openSignal={quickAddOpenSignal}
        prefillTime={dragCreateTime}
        onPrefillConsumed={() => setDragCreateTime(null)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 35,
    elevation: 9,
  },
});
